/**
 * Password Validation Utility
 *
 * Enforces the application's password strength policy during registration
 * and password change flows. The rules here must stay in sync with any
 * server-side password validation in the Laravel backend.
 *
 * Current policy:
 * - Minimum 12 characters
 * - At least 1 uppercase letter (A-Z)
 * - At least 1 lowercase letter (a-z)
 * - At least 1 digit (0-9)
 * - At least 1 special character (e.g., !@#$%^&*)
 */

/**
 * Represents the result of a password validation check.
 *
 * @property isValid - True if the password meets all requirements
 * @property errors - Array of human-readable error messages describing
 *					  which rules were violated (empty if isValid is true)
 */
export interface PasswordValidationResult {
	isValid: boolean;
	errors: string[];
}

/**
 * Validates a password against the application's strength requirements.
 *
 * Checks all rules and accumulates every violation (not just the first),
 * so the UI can display all issues to the user at once.
 *
 * @param password - The plaintext password string to validate
 * @returns A PasswordValidationResult with isValid flag and any error messages
 *
 * @example
 * validatePassword("short")
 * // { isValid: false, errors: ["Password must be at least 12 characters long", ...] }
 *
 * validatePassword("MyStr0ng!Pass#")
 * // { isValid: true, errors: [] }
 */
export function validatePassword(password: string): PasswordValidationResult {
	const errors: string[] = [];

	// Rule 1: Minimum length of 12 characters
	if (password.length < 12) {
		errors.push('Password must be at least 12 characters long');
	}
	// Rule 2: At least one uppercase letter (A-Z)
	if (!/[A-Z]/.test(password)) {
		errors.push('Password must contain at least 1 uppercase letter');
	}
	// Rule 3: At least one lowercase letter (a-z)
	if (!/[a-z]/.test(password)) {
		errors.push('Password must contain at least 1 lowercase letter');
	}
	// Rule 4: At least one numeric digit (0-9)
	if (!/[0-9]/.test(password)) {
		errors.push('Password must contain at least 1 number');
	}
	// Rule 5: At least one special character from a broad set of symbols
	if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) {
		errors.push('Password must contain at least 1 special character');
	}

	return {
		isValid: errors.length === 0,
		errors,
	};
}
